// src/HomePage/Game/FolkGame/Caro/CaroLogic.js

export const BOARD_SIZE = 15;

export const createEmptyBoard = () => {
  return Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(null));
};

export const normalizeBoard = (rawBoard) => {
  if (!rawBoard) return createEmptyBoard();
  const board = [];
  for (let r = 0; r < BOARD_SIZE; r++) {
    const row = [];
    const rawRow = rawBoard[r];
    for (let c = 0; c < BOARD_SIZE; c++) {
      const val = rawRow ? rawRow[c] : null;
      row.push(val === undefined ? null : val);
    }
    board.push(row);
  }
  return board;
};

/**
 * Kiểm tra 5 ô liên tiếp giành chiến thắng
 */
export const checkCaroWinner = (board, r, c) => {
  const current = board[r]?.[c];
  if (!current) return null;

  const directions = [
    [ [0, 1], [0, -1] ],   // Ngang
    [ [1, 0], [-1, 0] ],   // Dọc
    [ [1, 1], [-1, -1] ],  // Chéo chính \
    [ [1, -1], [-1, 1] ]   // Chéo phụ /
  ];

  for (const [dir1, dir2] of directions) {
    let count = 1;
    const winningLine = [[r, c]];

    let nr = r + dir1[0];
    let nc = c + dir1[1];
    while (nr >= 0 && nr < BOARD_SIZE && nc >= 0 && nc < BOARD_SIZE && board[nr]?.[nc] === current) {
      count++;
      winningLine.push([nr, nc]);
      nr += dir1[0];
      nc += dir1[1];
    }

    nr = r + dir2[0];
    nc = c + dir2[1];
    while (nr >= 0 && nr < BOARD_SIZE && nc >= 0 && nc < BOARD_SIZE && board[nr]?.[nc] === current) {
      count++;
      winningLine.push([nr, nc]);
      nr += dir2[0];
      nc += dir2[1];
    }

    if (count >= 5) {
      return { winner: current, winningLine };
    }
  }

  const isDraw = board.every((row) => row.every((cell) => cell !== null));
  if (isDraw) return { winner: 'DRAW', winningLine: [] };

  return null;
};

// ====================================================================
// THƯ VIỆN KHAI CUỘC (OPENING BOOK)
// ====================================================================
const getOpeningMove = (board) => {
  let moveCount = 0;
  let playerFirstMove = null;

  for (let r = 0; r < BOARD_SIZE; r++) {
    for (let c = 0; c < BOARD_SIZE; c++) {
      if (board[r][c]) {
        moveCount++;
        if (board[r][c] === 'X' && !playerFirstMove) {
          playerFirstMove = { r, c };
        }
      }
    }
  }

  // Nước 1: Bàn cờ trống -> Đánh chính giữa tâm (7, 7)
  if (moveCount === 0) {
    return { r: 7, c: 7 };
  }

  // Nước 2: Người chơi vừa đánh nước đầu tiên
  if (moveCount === 1 && playerFirstMove) {
    const { r, c } = playerFirstMove;
    // Nếu người chơi đánh tâm -> Bot đánh góc chéo kề (8, 8) hoặc (6, 6)
    if (r === 7 && c === 7) {
      return { r: 8, c: 8 };
    }
    // Nếu người chơi đánh xa tâm -> Bot chiếm ngay tâm (7, 7)
    if (!board[7][7]) {
      return { r: 7, c: 7 };
    }
  }

  // Nước 3: Bot mở màn (7,7), người chơi đánh nước 2 -> Bot hình thành thế hoa mai
  if (moveCount === 2 && board[7][7] === 'O') {
    const candidateOpenings = [
      { r: 8, c: 8 }, { r: 6, c: 6 }, { r: 6, c: 8 }, { r: 8, c: 6 },
      { r: 7, c: 8 }, { r: 7, c: 6 }, { r: 8, c: 7 }, { r: 6, c: 7 }
    ];
    for (const opt of candidateOpenings) {
      if (!board[opt.r][opt.c]) return opt;
    }
  }

  return null; // Kết thúc giai đoạn khai cuộc
};

// ====================================================================
// PATTERN MATRIX EVALUATOR (BẢNG ĐIỂM MẪU THẾ TRẬN)
// ====================================================================

/**
 * Đánh giá thế cờ trên 1 đường thẳng 9 ô quanh vị trí (r, c)
 */
const evaluateLinePattern = (lineStr, targetSymbol) => {
  const oppSymbol = targetSymbol === 'O' ? 'X' : 'O';

  // Chuẩn hóa chuỗi: Target='T', Opponent='O', Empty='.'
  const s = lineStr
    .split('')
    .map((ch) => (ch === targetSymbol ? 'T' : ch === oppSymbol ? 'O' : '.'))
    .join('');

  // 1. Thắng 5 ô
  if (s.includes('TTTTT')) return 100000;

  // 2. 4 ô mở 2 đầu (.TTTT.)
  if (s.includes('.TTTT.')) return 12000;

  // 3. 4 ô chặn 1 đầu hoặc 4 ô hở kẽ (OTTTT. / .TTTT O / TT.TT / T.TTT)
  if (
    s.includes('.TTTT') || s.includes('TTTT.') ||
    s.includes('TT.TT') || s.includes('T.TTT') || s.includes('TTT.T')
  ) {
    return 3500;
  }

  // 4. 3 ô mở 2 đầu (.TTT.) hoặc 3 ô so le (.T.TT. / .TT.T.)
  if (s.includes('.TTT.') || s.includes('.T.TT.') || s.includes('.TT.T.')) {
    return 3000;
  }

  // 5. 3 ô chặn 1 đầu (OTTT. / .TTTO)
  if (s.includes('.TTT') || s.includes('TTT.')) {
    return 600;
  }

  // 6. 2 ô mở 2 đầu (.TT.) hoặc (.T.T.)
  if (s.includes('.TT.') || s.includes('.T.T.')) {
    return 200;
  }

  // 7. 2 ô chặn 1 đầu
  if (s.includes('.TT') || s.includes('TT.')) {
    return 40;
  }

  return 0;
};

/**
 * Tính tổng điểm Công & Thủ cho một ô (r, c)
 */
const scoreCell = (board, r, c, botSymbol, difficulty = 'hard') => {
  const playerSymbol = botSymbol === 'O' ? 'X' : 'O';
  const directions = [
    [0, 1],  // Ngang
    [1, 0],  // Dọc
    [1, 1],  // Chéo \
    [1, -1]  // Chéo /
  ];

  let attackScore = 0;
  let defenseScore = 0;
  let attackThreeCount = 0;
  let defenseThreeCount = 0;

  for (const [dr, dc] of directions) {
    // Thu thập chuỗi 9 ô từ -4 đến +4 quanh ô đang xét
    let botLine = '';
    let playerLine = '';

    for (let step = -4; step <= 4; step++) {
      const nr = r + dr * step;
      const nc = c + dc * step;

      if (nr < 0 || nr >= BOARD_SIZE || nc < 0 || nc >= BOARD_SIZE) {
        botLine += 'X_OUT';
        playerLine += 'X_OUT';
      } else if (step === 0) {
        botLine += botSymbol;      // Giả lập bot đánh ô này
        playerLine += playerSymbol; // Giả lập người đánh ô này
      } else {
        const val = board[nr][nc];
        botLine += val || '.';
        playerLine += val || '.';
      }
    }

    const aScore = evaluateLinePattern(botLine, botSymbol);
    const dScore = evaluateLinePattern(playerLine, playerSymbol);

    attackScore += aScore;
    defenseScore += dScore;

    if (aScore >= 3000) attackThreeCount++;
    if (dScore >= 3000) defenseThreeCount++;
  }

  // Thưởng điểm cho đòn bẫy đôi (Nước cờ tạo 2 hướng 3 ô trở lên cùng lúc)
  if (attackThreeCount >= 2) attackScore += 9000;
  if (defenseThreeCount >= 2) defenseScore += 8000;

  // Ưu tiên ô gần tâm bàn cờ
  const centerBonus = 14 - (Math.abs(r - 7) + Math.abs(c - 7));

  if (difficulty === 'easy') {
    // Cấp Dễ: Chỉ nhận diện khi thắng 5 hoặc chặn 4, ít để ý nước 3
    return attackScore * 0.8 + defenseScore * 0.5 + centerBonus;
  }

  if (difficulty === 'medium') {
    // Cấp Vừa: Công và thủ cân bằng, nhận diện nước 3 mở
    return attackScore * 1.1 + defenseScore * 1.0 + centerBonus;
  }

  // Cấp Khó (Cao Thủ): Ưu tiên phòng thủ chặn các đòn chí mạng và tạo combo đôi
  return attackScore * 1.2 + defenseScore * 1.35 + centerBonus * 2;
};

/**
 * Hàm tìm nước đi thông minh nhất cho Bot theo cấp độ
 */
export const getBotCaroMove = (board, botSymbol = 'O', difficulty = 'hard') => {
  // 1. Áp dụng Khai Cuộc (Opening Book) nếu ở cấp Độ Khó / Trung Bình
  if (difficulty !== 'easy') {
    const openingMove = getOpeningMove(board);
    if (openingMove) return openingMove;
  }

  // 2. Tìm tất cả các ô trống có quân cờ lân cận trong bán kính 2 ô
  const candidateCells = [];
  for (let r = 0; r < BOARD_SIZE; r++) {
    for (let c = 0; c < BOARD_SIZE; c++) {
      if (!board[r][c]) {
        let hasNeighbor = false;
        for (let dr = -2; dr <= 2; dr++) {
          for (let dc = -2; dc <= 2; dc++) {
            const nr = r + dr;
            const nc = c + dc;
            if (nr >= 0 && nr < BOARD_SIZE && nc >= 0 && nc < BOARD_SIZE && board[nr][nc]) {
              hasNeighbor = true;
              break;
            }
          }
          if (hasNeighbor) break;
        }
        if (hasNeighbor) candidateCells.push({ r, c });
      }
    }
  }

  if (candidateCells.length === 0) {
    return { r: 7, c: 7 };
  }

  // 3. Chấm điểm từng ô bằng Ma Trận Mẫu Hình
  const scoredMoves = candidateCells.map((cell) => ({
    ...cell,
    score: scoreCell(board, cell.r, cell.c, botSymbol, difficulty)
  }));

  scoredMoves.sort((a, b) => b.score - a.score);

  // Cấp Dễ: 35% khả năng chọn ngẫu nhiên trong Top 4 ô tốt nhất
  if (difficulty === 'easy') {
    if (Math.random() < 0.35 && scoredMoves.length > 2) {
      const randomIdx = Math.floor(Math.random() * Math.min(4, scoredMoves.length));
      return scoredMoves[randomIdx];
    }
  }

  // Cấp Vừa: 15% chọn nước tốt thứ 2
  if (difficulty === 'medium') {
    if (Math.random() < 0.15 && scoredMoves.length > 1) {
      return scoredMoves[1];
    }
  }

  // Cấp Khó: Luôn chọn nước đi có điểm lượng giá cao nhất
  return scoredMoves[0];
};